import React, { FunctionComponent, useEffect, useReducer, useRef } from 'react';
import { Button, ChipHolder, Modal, ModalRef, Text, TextArea } from 'rs-emd-ui-atoms';
import { ProductPartNumber, ProductQuantity, ShareProductsType } from '../../../../../generated/graphql';
import { basketService } from '../../../../../services/basket-service';
import { getLabel } from '../../../helpers/html.utils';
import { ILabel } from '../../../models/common/label.model';
import styles from './share-products-modal-component.module.scss';

type ShareProductsModalProps = {
  labels: ILabel[];
  isCustomerLoggedIn: boolean;
  productQuantities: ProductQuantity[];
  productPartNumbers?: ProductPartNumber[];
  shareType: ShareProductsType;
  shareClickEventInfo?: string;
  onCloseCallback?: (isBasketShared: boolean) => void;
};

type State = {
  isCopyLinkBtnClicked: boolean;
  senderEmail?: string;
  senderEmailErrorMsg: string;
  recipientEmails: string[];
  optionalMsg?: string;
  isSharingProducts: boolean;
  areChipHolderEmailsValid: boolean;
};

enum ShareProductsModalActionType {
  SetIsCopyLinkBtnClicked = 'SetIsCopyLinkBtnClicked',
  SetShareProductsEmail = 'SetShareProductsEmail',
  SetShareProductsRecipientEmails = 'SetShareProductsRecipientEmails',
  SetShareProductsOptionalMsg = 'SetShareProductsOptionalMsg',
  SetIsSharingProducts = 'SetIsSharingProducts',
  SetSenderEmailErrorMsg = 'SetSenderEmailErrorMsg',
}

type Action =
  | { type: ShareProductsModalActionType.SetIsCopyLinkBtnClicked; isClicked: boolean }
  | { type: ShareProductsModalActionType.SetShareProductsEmail; email: string }
  | { type: ShareProductsModalActionType.SetShareProductsRecipientEmails; emails: string[]; areAllEmailsValid: boolean }
  | { type: ShareProductsModalActionType.SetShareProductsOptionalMsg; msg: string }
  | { type: ShareProductsModalActionType.SetIsSharingProducts; isSharing: boolean }
  | { type: ShareProductsModalActionType.SetSenderEmailErrorMsg; msg: string };

export const ShareProductsModal: FunctionComponent<ShareProductsModalProps> = (props: ShareProductsModalProps) => {
  const [state, dispatch] = useReducer(reducer, {
    isCopyLinkBtnClicked: false,
    recipientEmails: [],
    isSharingProducts: false,
    senderEmailErrorMsg: '',
    areChipHolderEmailsValid: true,
  });

  const modalRef = useRef<ModalRef>(null);

  useEffect(() => {
    if (state.isCopyLinkBtnClicked) {
      setTimeout(() => {
        dispatch({ type: ShareProductsModalActionType.SetIsCopyLinkBtnClicked, isClicked: false });
      }, 5000);
    }
  }, [state.isCopyLinkBtnClicked]);

  const shareUrl = `${window.location.origin}/cart?products=${props.productQuantities
    .map((pq) => `${pq.stockCode}_${pq.quantity}`)
    .join('|')}`;

  return (
    <Modal
      ref={modalRef}
      isSidebar={true}
      showSidebarHeaderBottomBorder={false}
      isMobileFullScreen={true}
      isWhiteBackgroundColor={true}
      className={styles['share-modal']}
      headerElt={getLabel(props.labels, 'share_basket_modal_title')}
      headerClassName={styles['share-modal-header']}
      closeCallback={() => props.onCloseCallback?.(false)}
      footerElt={
        <>
          <Button
            icon='link'
            text={getLabel(props.labels, state.isCopyLinkBtnClicked ? 'link_copied' : 'copy_link')}
            className={`${styles['btn-link']} ${styles['mobile-btn-link']}`}
            buttonType='ghost'
            onClick={() => {
              // Copy the share url
              navigator.clipboard.writeText(shareUrl);
              dispatch({ type: ShareProductsModalActionType.SetIsCopyLinkBtnClicked, isClicked: true });

              basketService.buttonClickEvent(
                'Share basket Copy link',
                props.productQuantities.map((pq) => pq.stockCode)
              );
            }}
          />
          <div className={styles['share-modal-footer']}>
            <Button
              icon='link'
              text={getLabel(props.labels, state.isCopyLinkBtnClicked ? 'link_copied' : 'copy_link')}
              className={styles['btn-link']}
              buttonType='ghost'
              onClick={() => {
                // Copy the share url
                navigator.clipboard.writeText(shareUrl);
                dispatch({ type: ShareProductsModalActionType.SetIsCopyLinkBtnClicked, isClicked: true });

                basketService.buttonClickEvent(
                  'Share basket Copy link',
                  props.productQuantities.map((pq) => pq.stockCode)
                );
              }}
            />
            <div className={styles['share-btns']}>
              <Button
                text={getLabel(props.labels, 'cancel')}
                buttonType='outline'
                onClick={(e: any) => modalRef.current?.closeModal(e)}
                disabled={state.isSharingProducts}
              />
              <Button
                text={getLabel(props.labels, 'share')}
                onClick={shareProducts}
                disabled={
                  state.recipientEmails.length === 0 ||
                  state.isSharingProducts ||
                  state.senderEmailErrorMsg?.length > 0 ||
                  !state.areChipHolderEmailsValid
                }
              />
            </div>
          </div>
        </>
      }>
      <div className={styles['share-modal-content']}>
        <div className={styles['share-form']}>
          {!props.isCustomerLoggedIn && (
            <div className={styles['share-modal-field']}>
              <p>{getLabel(props.labels, 'your_email')}</p>
              <Text
                value={state.senderEmail}
                placeholder={getLabel(props.labels, 'share_basket_email_placeholder')}
                onChange={(e: any) =>
                  dispatch({
                    type: ShareProductsModalActionType.SetShareProductsEmail,
                    email: e.currentTarget.value,
                  })
                }
                isValid={state.senderEmailErrorMsg === ''}
                errorMessage={state.senderEmailErrorMsg}
                validationCallback={(val: string) => validateEmailAddress(val, true)}
                disabled={state.isSharingProducts}
              />
            </div>
          )}
          <div className={styles['share-modal-field']}>
            <p>{getLabel(props.labels, 'recipient_emails')}</p>
            <ChipHolder
              placeholder={getLabel(props.labels, 'recipient_emails_placeholder')}
              onChipValidationCallback={(val: string) => validateEmailAddress(val)}
              onChipUpdateCallback={(validChips: string[], invalidChips: string[]) => {
                dispatch({
                  type: ShareProductsModalActionType.SetShareProductsRecipientEmails,
                  emails: validChips,
                  areAllEmailsValid: invalidChips.length === 0,
                });
              }}
              disabled={state.isSharingProducts}
              errorMessage={getLabel(props.labels, 'please_enter_a_valid_email_address')}
            />
          </div>
          <div className={styles['share-modal-field']}>
            <p>{getLabel(props.labels, 'share_basket_message_optional')}</p>
            <TextArea
              placeholder={getLabel(props.labels, 'share_basket_message_optional_placeholder')}
              isResizableVertically={true}
              disabled={state.isSharingProducts}
              onInput={(e: any) =>
                dispatch({
                  type: ShareProductsModalActionType.SetShareProductsOptionalMsg,
                  msg: e.currentTarget.value,
                })
              }
            />
          </div>
        </div>
      </div>
    </Modal>
  );

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case ShareProductsModalActionType.SetIsCopyLinkBtnClicked: {
        return {
          ...state,
          isCopyLinkBtnClicked: action.isClicked,
        };
      }
      case ShareProductsModalActionType.SetShareProductsEmail: {
        return {
          ...state,
          senderEmail: action.email,
        };
      }
      case ShareProductsModalActionType.SetShareProductsRecipientEmails: {
        return {
          ...state,
          recipientEmails: action.emails,
          areChipHolderEmailsValid: action.areAllEmailsValid,
        };
      }
      case ShareProductsModalActionType.SetShareProductsOptionalMsg: {
        return {
          ...state,
          optionalMsg: action.msg,
        };
      }
      case ShareProductsModalActionType.SetIsSharingProducts: {
        return {
          ...state,
          isSharingProducts: action.isSharing,
        };
      }
      case ShareProductsModalActionType.SetSenderEmailErrorMsg: {
        return {
          ...state,
          senderEmailErrorMsg: action.msg,
        };
      }
    }
  }

  function validateEmailAddress(val: string, isSenderEmail?: boolean) {
    let trimmedVal = val.trim();

    if (trimmedVal.length === 0) {
      return false;
    }

    let isValid = true;

    //\u00c0-\u017e --> https://port135.com/how-to-allow-european-characters-in-text-fields-by-using-regular-expression/
    if (trimmedVal.length > 0) {
      isValid =
        val.match(
          /^[a-zа-я\u00c0-\u017e\u0400-\u04ff\u0500-\u052F\ua640-\ua69F\u1d00-\u1d7f0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zа-я\u00c0-\u017e\u0400-\u04ff\u0500-\u052F\ua640-\ua69F\u1d00-\u1d7f0-9-]+\.(?:[.a-zа-я\u00c0-\u017e\u0400-\u04ff\u0500-\u052F\ua640-\ua69F\u1d00-\u1d7f0-9-]+)$/gim
        ) !== null;
    }

    if (isSenderEmail) {
      dispatch({
        type: ShareProductsModalActionType.SetSenderEmailErrorMsg,
        msg: isValid ? '' : getLabel(props.labels, 'please_enter_a_valid_email_address'),
      });
    }

    return isValid;
  }

  function shareProducts() {
    dispatch({ type: ShareProductsModalActionType.SetIsSharingProducts, isSharing: true });

    props.shareClickEventInfo &&
      basketService.buttonClickEvent(
        props.shareClickEventInfo,
        props.productQuantities.map((pq) => pq.stockCode)
      );

    basketService
      .shareProductsTransaction({
        senderEmail: state.senderEmail ?? null,
        recipientEmails: state.recipientEmails,
        optionalMessage: state.optionalMsg,
        productQuantities: props.productQuantities,
        productPartNumbers: props.productPartNumbers ?? [],
        shareType: props.shareType,
      })
      .then((res) => {
        dispatch({ type: ShareProductsModalActionType.SetIsSharingProducts, isSharing: false });

        if (props.onCloseCallback) props.onCloseCallback(res.isSuccess ?? false);
      });
  }
};

export default ShareProductsModal;
